<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1 class="text-h4 font-weight-bold primary--text">
          Campos Disponibles para Convenios
        </h1>
        <p class="text-subtitle2 grey--text mt-2">
          Gestiona los campos de golf disponibles para crear convenios
        </p>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <v-btn color="primary" large @click="crearNuevoCampo">
          <v-icon left>mdi-plus</v-icon>
          Nuevo Campo
        </v-btn>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

    <!-- Vista de Tarjetas -->
    <v-row v-if="campos.length > 0">
      <v-col
        v-for="campo in campos"
        :key="campo.id"
        cols="12"
        sm="6"
        md="4"
      >
        <v-card class="elevation-2 h-100 d-flex flex-column">
          <v-img
            :src="campo.imagen"
            height="200px"
            @error="usarImagenPorDefecto"
          ></v-img>

          <v-card-title class="pb-2">{{ campo.nombre }}</v-card-title>

          <v-divider></v-divider>

          <v-card-text class="flex-grow-1">
            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">UBICACIÓN</div>
              <div class="text-body2 mt-1">
                <v-icon small>mdi-map-marker</v-icon>
                {{ campo.ubicacion }}
              </div>
            </div>

            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">INFORMACIÓN</div>
              <div class="text-body2 mt-1">
                <div><strong>Par:</strong> {{ campo.par }}</div>
                <div><strong>Hoyos:</strong> {{ campo.hoyos }}</div>
                <div><strong>Distancia:</strong> {{ campo.distancia }} metros</div>
                <div><strong>Handicap:</strong> {{ campo.handicap }}</div>
              </div>
            </div>

            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">HORARIO</div>
              <div class="text-body2 mt-1">
                <div>{{ campo.horario_apertura }} - {{ campo.horario_cierre }}</div>
              </div>
            </div>

            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">TIPOS DE CONVENIOS</div>
              <div class="mt-2">
                <div v-if="campo.tipos_convenios.length === 0" class="text-caption italic grey--text">
                  Sin tipos asignados
                </div>
                <v-chip
                  v-for="tipo in campo.tipos_convenios"
                  :key="tipo"
                  small
                  class="mr-2 mb-2"
                >
                  {{ tipo }}
                </v-chip>
              </div>
            </div>

            <div v-if="campo.coordinador">
              <div class="text-caption font-weight-bold grey--text">COORDINADOR</div>
              <div class="text-body2 mt-1">
                <div><strong>{{ campo.coordinador }}</strong></div>
                <div><v-icon x-small>mdi-email</v-icon> {{ campo.coordinador_email }}</div>
                <div><v-icon x-small>mdi-phone</v-icon> {{ campo.coordinador_telefono }}</div>
              </div>
            </div>

            <div class="mt-4">
              <div class="text-caption font-weight-bold grey--text">CONTACTO DEL CAMPO</div>
              <div class="text-body2 mt-1">
                <div><v-icon x-small>mdi-email</v-icon> {{ campo.email }}</div>
                <div><v-icon x-small>mdi-phone</v-icon> {{ campo.telefono }}</div>
              </div>
            </div>

            <div v-if="campo.vigente_desde || campo.vigente_hasta" class="mt-4">
              <div class="text-caption font-weight-bold grey--text">VIGENCIA</div>
              <div class="text-body2 mt-1">
                <div v-if="campo.vigente_desde">Desde: {{ formatDate(campo.vigente_desde) }}</div>
                <div v-if="campo.vigente_hasta">Hasta: {{ formatDate(campo.vigente_hasta) }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-chip
              :color="campo.activo ? 'success' : 'error'"
              text-color="white"
              small
              class="mr-2"
            >
              {{ campo.activo ? 'Activo' : 'Inactivo' }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-btn small text @click="editarCampo(campo)">
              <v-icon small>mdi-pencil</v-icon>
              Editar
            </v-btn>
            <v-btn small text color="error" @click="confirmarEliminar(campo)">
              <v-icon small>mdi-trash-can</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="!loading">
      <v-col cols="12">
        <v-alert type="info" outlined>
          No hay campos registrados. Haz clic en "Nuevo Campo" para crear uno.
        </v-alert>
      </v-col>
    </v-row>

    <!-- Dialog de Edición/Creación -->
    <v-dialog v-model="mostrarFormulario" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary--text">
          {{ esModo === 'crear' ? 'Crear Nuevo Campo' : 'Editar Campo' }}
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-text-field
              v-model="formulario.name"
              label="Nombre"
              outlined
              dense
              required
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model="formulario.location"
              label="Ubicación"
              outlined
              dense
              required
              class="mb-4"
            ></v-text-field>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="formulario.par"
                  label="Par"
                  type="number"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="formulario.holes"
                  label="Hoyos"
                  type="number"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="formulario.distance"
                  label="Distancia (metros)"
                  type="number"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="formulario.handicap_rating"
                  label="Handicap"
                  type="number"
                  step="0.1"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formulario.opening_time"
                  label="Apertura"
                  type="time"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formulario.closing_time"
                  label="Cierre"
                  type="time"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-text-field
              v-model="formulario.email"
              label="Email"
              type="email"
              outlined
              dense
              class="mb-4"
              :error="formulario.email && !validarEmail(formulario.email)"
              :error-messages="formulario.email && !validarEmail(formulario.email) ? 'Email no válido' : ''"
            ></v-text-field>

            <v-row>
              <v-col cols="12" sm="5">
                <v-select
                  v-model="formulario.phone_code"
                  :items="paises"
                  item-text="pais"
                  item-value="codigo"
                  label="País"
                  outlined
                  dense
                  class="mb-4"
                >
                  <template v-slot:item="{ item }">
                    {{ item.pais }}
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.pais }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="formulario.phone"
                  label="Teléfono (10 dígitos)"
                  type="number"
                  outlined
                  dense
                  class="mb-4"
                  :error="formulario.phone && !validarTelefono(formulario.phone)"
                  :error-messages="formulario.phone && !validarTelefono(formulario.phone) ? 'Debe tener exactamente 10 dígitos' : ''"
                  @input="formulario.phone = formulario.phone.replace(/\D/g, '').slice(0, 10)"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="mb-4">
              <div class="text-subtitle2 font-weight-bold mb-2">Imagen del Campo</div>
              <div class="d-flex align-center gap-3">
                <v-file-input
                  ref="fileInput"
                  v-model="imagenSeleccionada"
                  label="Seleccionar imagen"
                  accept="image/*"
                  outlined
                  dense
                  show-size
                  class="flex-grow-1"
                  @change="onImagenSeleccionada"
                ></v-file-input>
              </div>
              <div v-if="previewImagen" class="mt-3">
                <div class="text-caption grey--text mb-2">Vista previa:</div>
                <v-img
                  :src="previewImagen"
                  height="150px"
                  class="rounded"
                ></v-img>
              </div>
              <div v-else-if="formulario.image && !imagenSeleccionada" class="mt-3">
                <div class="text-caption grey--text mb-2">Imagen actual:</div>
                <v-img
                  :src="formulario.image"
                  height="150px"
                  class="rounded"
                ></v-img>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formulario.valid_from"
                  label="Válido desde"
                  type="date"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="formulario.valid_until"
                  label="Válido hasta"
                  type="date"
                  outlined
                  dense
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-select
              v-model="formulario.agreement_types"
              :items="tiposConveniosDisponibles"
              item-text="name"
              item-value="id"
              label="Tipos de Convenios"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              class="mb-4"
            ></v-select>

            <v-checkbox
              v-model="formulario.is_active"
              label="Campo activo"
            ></v-checkbox>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cerrarFormulario">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="guardarCampo" :loading="guardando">
            {{ esModo === 'crear' ? 'Crear' : 'Guardar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmación de eliminación -->
    <v-dialog v-model="mostrarConfirmacion" max-width="400px">
      <v-card>
        <v-card-title class="error--text">
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar el campo "{{ campoAEliminar?.nombre }}"?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="mostrarConfirmacion = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="eliminarCampo">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom right timeout="4000">
      {{ snackbar.mensaje }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'GolfConveniosCampos',
  data () {
    return {
      mostrarFormulario: false,
      mostrarConfirmacion: false,
      esModo: 'crear',
      campoEditando: null,
      campoAEliminar: null,
      guardando: false,
      imagenSeleccionada: null,
      previewImagen: null,
      snackbar: {
        show: false,
        mensaje: '',
        color: 'success'
      },
      formulario: this.initFormulario(),
      tiposConveniosDisponibles: [],
      paises: [
        { codigo: '+1', pais: '🇺🇸 +1' },
        { codigo: '+52', pais: '🇲🇽 +52' },
        { codigo: '+34', pais: '🇪🇸 +34' },
        { codigo: '+55', pais: '🇧🇷 +55' },
        { codigo: '+56', pais: '🇨🇱 +56' },
        { codigo: '+57', pais: '🇨🇴 +57' },
        { codigo: '+58', pais: '🇻🇪 +58' },
        { codigo: '+51', pais: '🇵🇪 +51' },
        { codigo: '+54', pais: '🇦🇷 +54' },
        { codigo: '+591', pais: '🇧🇴 +591' },
        { codigo: '+598', pais: '🇺🇾 +598' },
        { codigo: '+64', pais: '🇳🇿 +64' },
        { codigo: '+61', pais: '🇦🇺 +61' },
        { codigo: '+44', pais: '🇬🇧 +44' },
        { codigo: '+33', pais: '🇫🇷 +33' },
        { codigo: '+49', pais: '🇩🇪 +49' },
        { codigo: '+39', pais: '🇮🇹 +39' },
        { codigo: '+31', pais: '🇳🇱 +31' },
        { codigo: '+46', pais: '🇸🇪 +46' },
        { codigo: '+47', pais: '🇳🇴 +47' },
        { codigo: '+353', pais: '🇮🇪 +353' },
        { codigo: '+32', pais: '🇧🇪 +32' },
        { codigo: '+43', pais: '🇦🇹 +43' },
        { codigo: '+41', pais: '🇨🇭 +41' },
        { codigo: '+45', pais: '🇩🇰 +45' },
        { codigo: '+48', pais: '🇵🇱 +48' },
        { codigo: '+420', pais: '🇨🇿 +420' },
        { codigo: '+36', pais: '🇭🇺 +36' },
        { codigo: '+40', pais: '🇷🇴 +40' },
        { codigo: '+30', pais: '🇬🇷 +30' },
        { codigo: '+381', pais: '🇷🇸 +381' },
        { codigo: '+385', pais: '🇭🇷 +385' },
        { codigo: '+386', pais: '🇸🇮 +386' },
        { codigo: '+359', pais: '🇧🇬 +359' },
        { codigo: '+372', pais: '🇪🇪 +372' },
        { codigo: '+371', pais: '🇱🇻 +371' },
        { codigo: '+370', pais: '🇱🇹 +370' },
        { codigo: '+358', pais: '🇫🇮 +358' },
        { codigo: '+1787', pais: '🇵🇷 +1787' },
        { codigo: '+971', pais: '🇦🇪 +971' },
        { codigo: '+966', pais: '🇸🇦 +966' },
        { codigo: '+60', pais: '🇲🇾 +60' },
        { codigo: '+65', pais: '🇸🇬 +65' },
        { codigo: '+81', pais: '🇯🇵 +81' },
        { codigo: '+86', pais: '🇨🇳 +86' },
        { codigo: '+91', pais: '🇮🇳 +91' },
        { codigo: '+82', pais: '🇰🇷 +82' }
      ]
    }
  },
  computed: {
    ...mapState('golfConvenios', {
      camposBackend: 'campos',
      loading: 'loading',
      error: 'error'
    }),
    campos () {
      return this.$store.getters['golfConvenios/camposNormalizados']
    }
  },
  mounted () {
    this.cargarDatos()
  },
  methods: {
    ...mapActions('golfConvenios', ['fetchCampos', 'updateCampo', 'createCampo', 'deleteCampo']),
    initFormulario () {
      return {
        name: '',
        location: '',
        par: 72,
        holes: 18,
        distance: 6500,
        handicap_rating: 72.0,
        opening_time: '06:00',
        closing_time: '18:00',
        email: '',
        phone_code: '+52',
        phone: '',
        agreement_types: [],
        is_active: true,
        valid_from: null,
        valid_until: null,
        image: null
      }
    },
    resetFormulario () {
      this.formulario = this.initFormulario()
      this.imagenSeleccionada = null
      this.previewImagen = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.clearableCallback()
      }
    },
    async cargarDatos () {
      try {
        await this.fetchCampos()
        await this.cargarTiposConvenios()
      } catch (error) {
        this.mostrarSnackbar('Error al cargar datos', 'error')
      }
    },
    async cargarTiposConvenios () {
      try {
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.tiposConvenios()

        let tipos = response.data.choices || response.data.results || response.data || []

        // Si vienen como tuplas [id, name], convertir a objetos {id, name}
        if (Array.isArray(tipos) && tipos.length > 0 && Array.isArray(tipos[0])) {
          tipos = tipos.map(([id, name]) => ({ id, name }))
        }

        this.tiposConveniosDisponibles = tipos
      } catch (error) {
        console.error('Error cargando tipos de convenios:', error)
        // Cargar tipos por defecto si falla
        this.tiposConveniosDisponibles = [
          { id: 1, name: 'Pago' },
          { id: 2, name: 'Cortesía' },
          { id: 3, name: 'Intercambio' },
          { id: 4, name: 'Privilegio' },
          { id: 5, name: 'Convenio especial' }
        ]
      }
    },
    crearNuevoCampo () {
      this.esModo = 'crear'
      this.campoEditando = null
      this.resetFormulario()
      this.mostrarFormulario = true
    },
    editarCampo (campo) {
      this.esModo = 'editar'
      this.campoEditando = campo
      this.imagenSeleccionada = null
      this.previewImagen = null
      const campoOriginal = this.$store.state.golfConvenios.campos.find(c => c.id === campo.id)
      this.formulario = {
        name: campoOriginal.name,
        location: campoOriginal.location,
        par: campoOriginal.par,
        holes: campoOriginal.holes,
        distance: campoOriginal.distance,
        handicap_rating: parseFloat(campoOriginal.handicap_rating) || 72.0,
        opening_time: campoOriginal.opening_time ? campoOriginal.opening_time.substring(0, 5) : '06:00',
        closing_time: campoOriginal.closing_time ? campoOriginal.closing_time.substring(0, 5) : '18:00',
        email: campoOriginal.email,
        phone_code: campoOriginal.phone_code || '+52',
        phone: campoOriginal.phone,
        agreement_types: campoOriginal.agreement_types || [],
        is_active: campoOriginal.is_active,
        valid_from: campoOriginal.valid_from,
        valid_until: campoOriginal.valid_until,
        image: campoOriginal.image || null
      }
      this.mostrarFormulario = true
    },
    cerrarFormulario () {
      this.mostrarFormulario = false
      this.campoEditando = null
      this.resetFormulario()
    },
    validarEmail (email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return regex.test(email)
    },
    validarTelefono (telefono) {
      const regex = /^\d{10}$/
      return regex.test(telefono)
    },
    async guardarCampo () {
      if (!this.$refs.form?.validate?.()) {
        this.mostrarSnackbar('Por favor completa los campos requeridos', 'warning')
        return
      }

      if (!this.validarEmail(this.formulario.email)) {
        this.mostrarSnackbar('El email no es válido', 'error')
        return
      }

      if (!this.validarTelefono(this.formulario.phone)) {
        this.mostrarSnackbar('El teléfono debe tener exactamente 10 dígitos', 'error')
        return
      }

      this.guardando = true
      try {
        let datos

        // Solo usar FormData si hay imagen nueva (en crear o editar con imagen)
        if (this.imagenSeleccionada) {
          const formData = new FormData()
          formData.append('name', this.formulario.name)
          formData.append('location', this.formulario.location)
          formData.append('par', this.formulario.par)
          formData.append('holes', this.formulario.holes)
          formData.append('distance', this.formulario.distance)
          formData.append('handicap_rating', this.formulario.handicap_rating)
          formData.append('opening_time', this.formulario.opening_time + ':00')
          formData.append('closing_time', this.formulario.closing_time + ':00')
          formData.append('email', this.formulario.email)
          formData.append('phone_code', this.formulario.phone_code)
          formData.append('phone', this.formulario.phone)
          formData.append('is_active', this.formulario.is_active)

          if (this.formulario.valid_from) {
            formData.append('valid_from', this.formulario.valid_from)
          }
          if (this.formulario.valid_until) {
            formData.append('valid_until', this.formulario.valid_until)
          }

          this.formulario.agreement_types.forEach((tipo, index) => {
            formData.append(`agreement_types[${index}]`, tipo)
          })

          formData.append('image', this.imagenSeleccionada)
          datos = formData
        } else {
          // Si no hay imagen nueva, enviar como JSON
          datos = {
            name: this.formulario.name,
            location: this.formulario.location,
            par: this.formulario.par,
            holes: this.formulario.holes,
            distance: this.formulario.distance,
            handicap_rating: this.formulario.handicap_rating,
            opening_time: this.formulario.opening_time + ':00',
            closing_time: this.formulario.closing_time + ':00',
            email: this.formulario.email,
            phone_code: this.formulario.phone_code,
            phone: this.formulario.phone,
            is_active: this.formulario.is_active,
            agreement_types: this.formulario.agreement_types
          }

          if (this.formulario.valid_from) {
            datos.valid_from = this.formulario.valid_from
          }
          if (this.formulario.valid_until) {
            datos.valid_until = this.formulario.valid_until
          }
        }

        if (this.esModo === 'crear') {
          await this.createCampo(datos)
          this.mostrarSnackbar('✓ Campo creado exitosamente', 'success')
        } else {
          await this.updateCampo({
            id: this.campoEditando.id,
            data: datos
          })
          this.mostrarSnackbar('✓ Campo actualizado exitosamente', 'success')
        }

        await this.cargarDatos()
        this.cerrarFormulario()
      } catch (error) {
        console.error('Error:', error)
        this.mostrarSnackbar(error.message || 'Error al guardar campo', 'error')
      } finally {
        this.guardando = false
      }
    },
    onImagenSeleccionada () {
      if (!this.imagenSeleccionada) {
        this.previewImagen = null
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        this.previewImagen = e.target.result
      }
      reader.readAsDataURL(this.imagenSeleccionada)
    },
    confirmarEliminar (campo) {
      this.campoAEliminar = campo
      this.mostrarConfirmacion = true
    },
    async eliminarCampo () {
      try {
        await this.deleteCampo(this.campoAEliminar.id)
        this.mostrarSnackbar('✓ Campo eliminado exitosamente', 'success')
        await this.cargarDatos()
      } catch (error) {
        this.mostrarSnackbar('Error al eliminar campo', 'error')
      } finally {
        this.mostrarConfirmacion = false
        this.campoAEliminar = null
      }
    },
    mostrarSnackbar (mensaje, color) {
      this.snackbar = {
        show: true,
        mensaje,
        color
      }
    },
    formatDate (fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleDateString('es-ES')
    },
    usarImagenPorDefecto (event) {
      event.target.src = 'https://via.placeholder.com/400x200?text=Campo+de+Golf'
    }
  }
}
</script>
